import { gql, useMutation } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { BATCH_UPDATE_TALENT_FAVORITES_UPERCUT } from '@gql/talentFavorites';
import { UPDATE_JOB_APPLICATION_RATING, UPDATE_JOB_APPLICATION_STATUS } from '@gql/jobs';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { MultiSelectJobApplication } from '../types';
import { getMultiSelectJobApplicationStatus } from '../utils';

type UpercutFavoriteType = 'public' | 'private';

interface JobApplicationsOperationsContextValue {
    // Selection state
    selectedJobApplications: MultiSelectJobApplication[];
    selectedJobApplicationIds: string[];
    currentStatus: string;
    addJobApplication: (jobApplicationId: string, status?: string, talentId?: string) => void;
    removeJobApplication: (jobApplicationId: string) => void;
    toggleJobApplication: (jobApplicationId: string, status?: string, talentId?: string) => void;
    clearSelection: () => void;
    isSelected: (jobApplicationId: string) => boolean;

    // Operations
    updateHiringStatus: (jobApplicationIds: string[], status: string, subStatus: string) => Promise<void>;
    updateRating: (jobApplicationIds: string[], rating: number) => Promise<void>;
    addToFavorites: (
        talentIds: string[],
        currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string }>,
        initialTypes: UpercutFavoriteType[],
        newTypes: UpercutFavoriteType[]
    ) => Promise<void>;

    // Loading states
    isUpdatingStatus: boolean;
    isUpdatingRating: boolean;
    isUpdatingFavorites: boolean;

    // Convenience methods operating on selected job applications
    updateSelectedHiringStatus: (status: string, subStatus: string) => Promise<void>;
    updateSelectedRating: (rating: number) => Promise<void>;
    addSelectedToFavorites: (
        currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string }>,
        initialTypes: UpercutFavoriteType[],
        newTypes: UpercutFavoriteType[]
    ) => Promise<void>;
}

const JobApplicationsOperationsContext = createContext<JobApplicationsOperationsContextValue | null>(null);

export const useJobApplicationsOperations = (): JobApplicationsOperationsContextValue => {
    const ctx = useContext(JobApplicationsOperationsContext);
    if (!ctx) {
        throw new Error('useJobApplicationsOperations must be used within a <JobApplicationsOperationsProvider>');
    }
    return ctx;
};

interface JobApplicationsOperationsProviderProps {
    children: React.ReactNode;
}

export const JobApplicationsOperationsProvider: React.FC<JobApplicationsOperationsProviderProps> = ({
    children,
}) => {
    const { showSnackbar } = useSnackbar();
    const { isUpercut } = useAuth();

    // Selection state
    const [selectedJobApplications, setSelectedJobApplications] = useState<MultiSelectJobApplication[]>([]);

    const selectedJobApplicationIds = useMemo(
        () => selectedJobApplications.map(ja => ja._id),
        [selectedJobApplications]
    );

    const currentStatus = useMemo(
        () => getMultiSelectJobApplicationStatus(selectedJobApplications),
        [selectedJobApplications]
    );

    // Track loading manually since we use Promise.all with single-entity mutations
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isUpdatingRating, setIsUpdatingRating] = useState(false);
    const [batchUpdateFavoritesUpercut, { loading: isUpdatingFavorites }] = useMutation(
        BATCH_UPDATE_TALENT_FAVORITES_UPERCUT
    );
    const [updateStatusMutation] = useMutation(UPDATE_JOB_APPLICATION_STATUS);
    const [updateRatingMutation] = useMutation(UPDATE_JOB_APPLICATION_RATING);

    // Selection methods
    const addJobApplication = useCallback(
        (jobApplicationId: string, status?: string, talentId?: string) => {
            setSelectedJobApplications(prev => {
                if (prev.some(ja => ja._id === jobApplicationId)) return prev;
                return [...prev, { _id: jobApplicationId, status, talentId }];
            });
        },
        []
    );

    const removeJobApplication = useCallback((jobApplicationId: string) => {
        setSelectedJobApplications(prev => prev.filter(ja => ja._id !== jobApplicationId));
    }, []);

    const toggleJobApplication = useCallback(
        (jobApplicationId: string, status?: string, talentId?: string) => {
            setSelectedJobApplications(prev => {
                const exists = prev.some(ja => ja._id === jobApplicationId);
                if (exists) return prev.filter(ja => ja._id !== jobApplicationId);
                return [...prev, { _id: jobApplicationId, status, talentId }];
            });
        },
        []
    );

    const clearSelection = useCallback(() => {
        setSelectedJobApplications([]);
    }, []);

    const isSelected = useCallback(
        (jobApplicationId: string) => selectedJobApplications.some(ja => ja._id === jobApplicationId),
        [selectedJobApplications]
    );

    // Operations
    const updateHiringStatus = useCallback(
        async (jobApplicationIds: string[], status: string, subStatus: string) => {
            setIsUpdatingStatus(true);
            try {
                await Promise.all(
                    jobApplicationIds.map(id =>
                        updateStatusMutation({
                            variables: { _id: id, status, subStatus },
                            update: (cache, { data }) => {
                                if (data?.updateJobApplication) {
                                    const cacheId = cache.identify({
                                        __typename: 'JobApplication',
                                        _id: id,
                                    });
                                    if (cacheId) {
                                        cache.modify({
                                            id: cacheId,
                                            fields: {
                                                status: () => status,
                                                subStatus: () => subStatus,
                                            },
                                        });
                                    }
                                }
                            },
                        })
                    )
                );
                showSnackbar(
                    `Job application status${jobApplicationIds.length > 1 ? 'es' : ''} updated successfully`,
                    'success'
                );
            } catch (error: any) {
                showSnackbar(error?.message ?? 'Error updating status', 'error');
            } finally {
                setIsUpdatingStatus(false);
            }
        },
        [updateStatusMutation, showSnackbar]
    );

    const updateRating = useCallback(
        async (jobApplicationIds: string[], rating: number) => {
            setIsUpdatingRating(true);
            try {
                await Promise.all(
                    jobApplicationIds.map(id =>
                        updateRatingMutation({
                            variables: { jobApplicationId: id, rating },
                            update: (cache, { data }) => {
                                if (data?.updateJobApplicationRatingUpercut) {
                                    const updated = data.updateJobApplicationRatingUpercut;
                                    cache.modify({
                                        id: cache.identify({ __typename: 'JobApplication', _id: updated._id }),
                                        fields: {
                                            upercutAverageRating: () => updated.upercutAverageRating,
                                            upercutRatings: () => updated.upercutRatings,
                                        },
                                    });
                                }
                            },
                        })
                    )
                );
                showSnackbar(
                    `Job application rating${jobApplicationIds.length > 1 ? 's' : ''} updated successfully`,
                    'success'
                );
            } catch (error: any) {
                showSnackbar(error?.message ?? 'Error updating rating', 'error');
            } finally {
                setIsUpdatingRating(false);
            }
        },
        [updateRatingMutation, showSnackbar]
    );

    const addToFavorites = useCallback(
        async (
            talentIds: string[],
            currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string }>,
            initialTypes: UpercutFavoriteType[],
            newTypes: UpercutFavoriteType[]
        ) => {
            if (!isUpercut) {
                console.warn('Attempted to update talent favorites for non-Upercut user');
                return;
            }

            try {
                const toAdd = newTypes.filter(type => !initialTypes.includes(type));
                const toRemove = initialTypes.filter(type => !newTypes.includes(type));
                const currentFavoriteIds = currentFavorites.map(fav => fav._id);

                await batchUpdateFavoritesUpercut({
                    variables: {
                        input: {
                            talentIds,
                            currentFavoriteIds,
                            toAdd: toAdd.length > 0 ? toAdd : undefined,
                            toRemove: toRemove.length > 0 ? toRemove : undefined,
                            metadata: undefined,
                        },
                    },
                    refetchQueries: ['GetTalentFavoritesUpercut'],
                    update: (cache, { data }) => {
                        if (data?.batchUpdateTalentFavoritesUpercut) {
                            const { added, removed } = data.batchUpdateTalentFavoritesUpercut;

                            // Update Talent entities (used by TalentDataTable / TalentFavoritesDataTable)
                            talentIds.forEach(talentId => {
                                const id =  cache.identify({ __typename: 'TalentPublic', _id: talentId });
                                cache.modify({
                                    id,
                                    fields: {
                                        upercutFavorites(existingFavorites = []) {
                                            let updatedFavorites = [...existingFavorites];

                                            if (removed.length > 0) {
                                                updatedFavorites = updatedFavorites.filter((favRef: any) => {
                                                    const favId = favRef.__ref?.split(':')[1];
                                                    return !removed.includes(favId);
                                                });
                                            }

                                            if (added.length > 0) {
                                                const newFavRefs = added.map((fav: any) => {
                                                    const transformedFav = {
                                                        _id: fav._id,
                                                        talentId,
                                                        type: fav.type,
                                                        metadata: fav.metadata,
                                                        createdAt: fav.createdAt,
                                                        __typename: 'TalentFavorite',
                                                    };
                                                    return cache.writeFragment({
                                                        data: transformedFav,
                                                        fragment: gql`
                                                            fragment NewFavorite on TalentFavorite {
                                                                _id
                                                                talentId
                                                                type
                                                                metadata {
                                                                    notes
                                                                    tags
                                                                    priority
                                                                    campaign
                                                                }
                                                                createdAt
                                                            }
                                                        `,
                                                    });
                                                });
                                                updatedFavorites = [...updatedFavorites, ...newFavRefs];
                                            }

                                            return updatedFavorites;
                                        },
                                    },
                                });
                            });

                            // Evict job application search results — forces a fresh refetch
                            // which will include the updated upercutFavorites from the backend
                            cache.evict({ fieldName: 'searchJobApplicationsUpercut' });
                            cache.evict({ fieldName: 'searchJobApplicationsPro' });
                            cache.gc();
                        }
                    },
                });

                showSnackbar(
                    `Talent${talentIds.length > 1 ? 's' : ''} added to favorites successfully`,
                    'success'
                );
            } catch (error) {
                console.error('Error updating favorites:', error);
                showSnackbar('Error updating favorites', 'error');
            }
        },
        [batchUpdateFavoritesUpercut, showSnackbar, isUpercut]
    );

    // Convenience methods operating on the current selection
    const updateSelectedHiringStatus = useCallback(
        (status: string, subStatus: string) => {
            return updateHiringStatus(selectedJobApplicationIds, status, subStatus);
        },
        [selectedJobApplicationIds, updateHiringStatus]
    );

    const updateSelectedRating = useCallback(
        (rating: number) => {
            return updateRating(selectedJobApplicationIds, rating);
        },
        [selectedJobApplicationIds, updateRating]
    );

    const addSelectedToFavorites = useCallback(
        (
            currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string }>,
            initialTypes: UpercutFavoriteType[],
            newTypes: UpercutFavoriteType[]
        ) => {
            const talentIds = selectedJobApplications
                .map(ja => ja.talentId)
                .filter((id): id is string => Boolean(id));
            return addToFavorites(talentIds, currentFavorites, initialTypes, newTypes);
        },
        [selectedJobApplications, addToFavorites]
    );

    const value = useMemo<JobApplicationsOperationsContextValue>(
        () => ({
            selectedJobApplications,
            selectedJobApplicationIds,
            currentStatus,
            addJobApplication,
            removeJobApplication,
            toggleJobApplication,
            clearSelection,
            isSelected,
            updateHiringStatus,
            updateRating,
            addToFavorites,
            isUpdatingStatus,
            isUpdatingRating,
            isUpdatingFavorites,
            updateSelectedHiringStatus,
            updateSelectedRating,
            addSelectedToFavorites,
        }),
        [
            selectedJobApplications,
            selectedJobApplicationIds,
            currentStatus,
            addJobApplication,
            removeJobApplication,
            toggleJobApplication,
            clearSelection,
            isSelected,
            updateHiringStatus,
            updateRating,
            addToFavorites,
            isUpdatingStatus,
            isUpdatingRating,
            isUpdatingFavorites,
            updateSelectedHiringStatus,
            updateSelectedRating,
            addSelectedToFavorites,
        ]
    );

    return (
        <JobApplicationsOperationsContext.Provider value={value}>
            {children}
        </JobApplicationsOperationsContext.Provider>
    );
};
